import { useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, ScanLine, X } from "lucide-react";
import { devLog } from "@/lib/devLog";

/**
 * BarcodeScanner — opens a modal and streams the device camera into a
 * <video>, then hands the live element off to ZXing for decoding.
 *
 * Why we manage the MediaStream ourselves (instead of using
 * `decodeFromConstraints`):
 *
 *   @zxing/browser 0.1.5's `decodeFromConstraints` calls `getUserMedia`,
 *   attaches the stream, and starts decoding all inside one promise.
 *   On Chrome / Edge for Windows + Android we've observed the stream
 *   getting attached but `<video>.play()` being skipped — net effect:
 *   black video, but decoding still fires when a barcode happens to be
 *   in front of the camera (because the frames ARE flowing to the
 *   decoder, just not to the visible <video> element).
 *
 * Our approach:
 *   1. Acquire the MediaStream ourselves with explicit constraints.
 *   2. Set it as `video.srcObject`, await `loadedmetadata`, then call
 *      `video.play()` — always, regardless of browser quirks.
 *   3. Once the video is playing, hand the element to ZXing's
 *      `decodeFromVideoElement` which now only does decoding (the
 *      stream-acquire path was the buggy part).
 *
 * Also keeps a scoped `unhandledrejection` listener to swallow the
 * harmless `set photo options failed` ZXing emits on some Androids.
 */
export default function BarcodeScanner({ onScan, label = "Scan" }) {
  const [open, setOpen] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const readerRef = useRef(null);
  const [error, setError] = useState("");
  const [stage, setStage] = useState("idle"); // idle | requesting | live | failed

  useEffect(() => {
    if (!open) return undefined;
    setError("");
    setStage("requesting");
    let attachedVideo = null;

    // Swallow the harmless photo-options rejection while the scanner
    // modal is open (see file docstring for context).
    const onUnhandled = (ev) => {
      const msg = String(ev.reason?.message || ev.reason || "");
      if (
        msg.includes("set photo options failed") ||
        msg.includes("setOptions") ||
        msg.includes("applyConstraints")
      ) {
        ev.preventDefault();
      }
    };
    window.addEventListener("unhandledrejection", onUnhandled);

    let cancelled = false;
    const reader = new BrowserMultiFormatReader();
    readerRef.current = reader;

    (async () => {
      try {
        // Step 1 — get the stream ourselves with explicit rear-cam,
        // sane resolution. No `advanced` constraints (Chrome rejects
        // them with "set photo options failed").
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;

        // Step 2 — wait for the video element to be mounted. If the
        // Dialog hasn't finished animating in yet, retry on the next
        // animation frame. This race is the other half of why the
        // preview sometimes stayed black.
        const waitForVideo = () =>
          new Promise((resolve) => {
            const check = () => {
              if (cancelled || videoRef.current) return resolve(videoRef.current);
              requestAnimationFrame(check);
            };
            check();
          });
        const video = await waitForVideo();
        attachedVideo = video;
        if (cancelled || !video) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }

        // Step 3 — attach & play. We bind every event we might need to
        // either start playback or surface a visible error.
        video.srcObject = stream;
        video.setAttribute("playsinline", "true");
        video.muted = true;
        await new Promise((resolve) => {
          const ready = () => {
            video.removeEventListener("loadedmetadata", ready);
            resolve();
          };
          if (video.readyState >= 1) resolve();
          else video.addEventListener("loadedmetadata", ready);
        });
        try {
          await video.play();
        } catch (playErr) {
          // Autoplay can be denied on some browsers; the user-gesture
          // requirement is already satisfied by their tap-to-scan, but
          // surface a hint just in case.
          devLog("[scanner] play() rejected:", playErr);
        }
        if (cancelled) return;
        setStage("live");

        // Step 4 — hand the live element to ZXing for decoding.
        reader.decodeFromVideoElement(video, (result) => {
          if (cancelled) return;
          if (result) {
            onScan?.(result.getText());
            // Close the modal — the cleanup function will tear down
            // the stream + reader.
            setOpen(false);
          }
        });
      } catch (err) {
        if (cancelled) return;
        const msg = String(err?.message || err || "");
        setStage("failed");
        if (err?.name === "NotAllowedError") {
          setError(
            "Camera permission denied. Grant access in your browser settings, then reopen.",
          );
        } else if (err?.name === "NotFoundError") {
          setError("No camera found on this device. Use the hardware scanner instead.");
        } else if (
          msg.includes("set photo options failed") ||
          msg.includes("applyConstraints") ||
          msg.includes("setOptions")
        ) {
          // Harmless — decoding actually works. Stay in "live" stage.
        } else {
          setError(`Camera failed to start: ${msg}`);
        }
      }
    })();

    return () => {
      cancelled = true;
      window.removeEventListener("unhandledrejection", onUnhandled);
      try {
        readerRef.current?.reset?.();
      } catch (err) {
        if (process.env.NODE_ENV !== "production") {
          devLog("Scanner reset failed", err);
        }
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      if (attachedVideo) {
        attachedVideo.srcObject = null;
      }
    };
  }, [open, onScan]);

  return (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={() => setOpen(true)}
        data-testid="open-scanner-button"
        className="h-12 gap-2"
      >
        <Camera className="h-4 w-4" /> {label}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="scanner-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <ScanLine className="h-5 w-5 text-primary" /> Camera scanner
            </DialogTitle>
          </DialogHeader>
          <div className="rounded-lg overflow-hidden bg-black aspect-video relative">
            <video
              ref={videoRef}
              className="w-full h-full object-cover"
              autoPlay
              muted
              playsInline
              data-testid="scanner-video"
            />
            {stage !== "live" && (
              <div className="absolute inset-0 flex items-center justify-center text-white text-sm pointer-events-none">
                {stage === "requesting" && "Starting camera…"}
                {stage === "failed" && (error || "Camera failed")}
                {stage === "idle" && ""}
              </div>
            )}
            <div className="absolute inset-0 border-2 border-primary/60 rounded-lg pointer-events-none" />
          </div>
          {error && stage === "failed" && (
            <div className="text-sm text-destructive">{error}</div>
          )}
          <div className="text-sm text-stone-600">
            Point the device camera at a barcode or QR. Hardware (USB / Bluetooth)
            scanners also work — focus an input field and scan.
          </div>
          <div className="flex justify-end">
            <Button
              variant="ghost"
              onClick={() => setOpen(false)}
              data-testid="close-scanner-button"
            >
              <X className="h-4 w-4 mr-2" /> Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
